import {
  Draggable,
  type DraggableProvided,
  Droppable,
  type DroppableProvided,
} from "@hello-pangea/dnd"
import { forEach } from "lodash"
import { useEffect, useState } from "react"
import styled from "styled-components"

import { type Uuid } from "#customTypes/globalTypes"

import { useCurriculumFormContext } from "./AddCurriculumFormContext"
import ProjectTimelineSegment from "./ProjectTimelineSegment"

const STimeline = styled.div`
  height: 40px;
  width: 100%;
  background-color: #f0f0f0;
  position: relative;
  border-radius: 4px;
  overflow: visible;
`

// Overflow hatching overlay - sits on top of projects past session limit
const SOverflowOverlay = styled.div<{ $leftPercent: number }>`
  position: absolute;
  left: ${(props) => props.$leftPercent}%;
  right: 0;
  height: 100%;
  pointer-events: none;
  z-index: 3;
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 8px,
    rgba(255, 50, 50, 0.25) 8px,
    rgba(255, 50, 50, 0.25) 16px
  );
`

const SSessionLimitMarker = styled.div<{ $leftPercent: number }>`
  position: absolute;
  left: ${(props) => props.$leftPercent}%;
  height: calc(100% + 4px);
  width: 2px;
  top: -2px;
  background-color: rgba(255, 50, 50, 0.8);
  z-index: 4;
`

const SSessionLimitLabel = styled.div<{ $leftPercent: number }>`
  position: absolute;
  left: ${(props) => props.$leftPercent}%;
  top: -20px;
  transform: translateX(-50%);
  color: rgba(255, 50, 50, 0.8);
  font-size: 12px;
  white-space: nowrap;
  z-index: 4;
`

const baseSegmentStyle = {
  position: "absolute" as const,
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontSize: 12,
  overflow: "visible",
  whiteSpace: "nowrap" as const,
  transition: "all 0.4s ease",
  cursor: "grab",
  transform: "translateY(-4px)",
}

const selectedSegmentStyle = {
  ...baseSegmentStyle,
  borderBottom: "3px solid #0066cc",
  zIndex: 2,
  transform: "translateY(-6px)",
}

// Colorblind-friendly palette (ColorBrewer Set3)
const colorPalette = [
  "#8dd3c7", // Teal
  "#bebada", // Light Purple
  "#fb8072", // Salmon
  "#80b1d3", // Sky Blue
  "#fdb462", // Light Orange
  "#b3de69", // Light Green
  "#fccde5", // Pink
  "#d9d9d9", // Light Gray
  "#bc80bd", // Purple
  "#ccebc5", // Pale Green
  "#ffed6f", // Light Yellow
  "#ffffb3", // Pale Yellow
  "#a6cee3", // Light Blue
  "#fb9a99", // Light Red
  "#e31a1c", // Dark Red
  "#33a02c", // Dark Green
  "#b2df8a", // Light Green
  "#1f78b4", // Dark Blue
  "#fdbf6f", // Orange
  "#ff7f00", // Dark Orange
] as const

const ProjectTimeline = ({
  numberOfSessions = 0,
  totalDays,
  selectedProjectId,
  setSelectedProjectId,
}: {
  numberOfSessions: number
  totalDays: number
  selectedProjectId: Uuid | null
  setSelectedProjectId: (projectId: Uuid) => void
}) => {
  const { projectsById, projectIds } = useCurriculumFormContext()
  const [projectColors] = useState<Map<Uuid, string>>(new Map())

  // Assign colors to new projects
  useEffect(() => {
    forEach(projectIds, (projectId) => {
      if (!projectColors.has(projectId)) {
        const colorIndex = projectColors.size % colorPalette.length
        projectColors.set(projectId, colorPalette[colorIndex])
      }
    })
  }, [projectIds, projectColors])

  if (!projectIds?.length) return null

  const effectiveDays = Math.max(numberOfSessions, totalDays)
  const hasOverflow = totalDays > numberOfSessions
  const sessionLimitPercent =
    numberOfSessions > 0 ? (numberOfSessions / effectiveDays) * 100 : 0

  // Pre-calculate left positions for all projects in a single pass
  const leftByProjectId: Record<Uuid, number> = {}
  let cumulativeLeft = 0
  for (const projectId of projectIds) {
    leftByProjectId[projectId] = cumulativeLeft
    const days = parseInt(projectsById[projectId]?.daysToComplete ?? "0")
    cumulativeLeft += (days / effectiveDays) * 100
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        margin: "20px 0",
      }}
    >
      <STimeline>
        {/* Session limit marker and overflow hatching overlay */}
        {hasOverflow && (
          <>
            <SSessionLimitMarker $leftPercent={sessionLimitPercent} />
            <SSessionLimitLabel $leftPercent={sessionLimitPercent}>
              Session Limit
            </SSessionLimitLabel>
            <SOverflowOverlay $leftPercent={sessionLimitPercent} />
          </>
        )}

        {/* Single Droppable for all projects */}
        <Droppable droppableId="timeline" direction="horizontal">
          {(provided: DroppableProvided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                position: "absolute",
                left: 0,
                width: "100%",
                height: "100%",
              }}
            >
              {projectIds.map((projectId, index) => {
                const project = projectsById[projectId]
                const left = leftByProjectId[projectId]

                // Show placeholder if project hasn't loaded yet
                if (!project) {
                  const width = 5 // Placeholder width as percentage
                  return (
                    <Draggable
                      draggableId={projectId}
                      index={index}
                      key={projectId}
                    >
                      {(dragProvided: DraggableProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          style={{
                            ...(selectedProjectId === projectId
                              ? selectedSegmentStyle
                              : baseSegmentStyle),
                            left: `${left}%`,
                            width: `${width}%`,
                            backgroundColor: "#d9d9d9",
                            fontSize: 16,
                            cursor: "grab",
                            position: "absolute",
                            top: 0,
                            ...dragProvided.draggableProps.style, // Must be last for DnD transforms
                          }}
                          title="Loading project or project not found"
                          onClick={() => setSelectedProjectId(projectId)}
                        >
                          ⚠️
                        </div>
                      )}
                    </Draggable>
                  )
                }

                return (
                  <ProjectTimelineSegment
                    key={project.id}
                    project={project}
                    effectiveDays={effectiveDays}
                    numberOfSessions={numberOfSessions}
                    index={index}
                    left={left}
                    style={{
                      ...(selectedProjectId === project.id
                        ? selectedSegmentStyle
                        : baseSegmentStyle),
                      backgroundColor: projectColors.get(project.id),
                    }}
                    onClick={() => setSelectedProjectId(project.id)}
                  />
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </STimeline>
    </div>
  )
}

export default ProjectTimeline
